// src/components/MultiSelecteDropdown.js
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import arrowIcon from "../assets/down-arrow.png";
import { useTranslation } from "react-i18next";
import CustomCheckbox from "./CustomCheckbox";

const MultiSelecteDropdown = ({
  options,
  selectedItems = [], // Array of selected items
  setSelectedItems, // Function to update selected items array
  thick,
  flex,
  placeholder = "Select options",
  disabled,
  titleCssClass,
  nameInAr = false,
  showSelectedCount = true, // Show "X selected" in the toggle
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const { t } = useTranslation("general");

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is outside both the dropdown toggle AND the menu
      const isOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideMenu = !event.target.closest(".dropdown-menu-portal");

      if (isOutsideDropdown && isOutsideMenu) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  // Update menu position when dropdown opens or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && toggleRef.current) {
        const rect = toggleRef.current.getBoundingClientRect();
        const menuHeight = menuRef.current ? menuRef.current.offsetHeight : 300; // Fallback to max height
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Determine if dropdown should open upward
        const shouldOpenUpward =
          spaceBelow < menuHeight && spaceAbove > spaceBelow;
        setOpenUpward(shouldOpenUpward);

        // Calculate position based on direction
        const top = shouldOpenUpward
          ? rect.top + window.scrollY - menuHeight - 5 // Above the toggle
          : rect.bottom + window.scrollY + 5; // Below the toggle

        setMenuPosition({
          top: top,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item) => {
    // Check if item is already selected
    const isSelected = selectedItems.some(
      (selected) => selected.id === item.id
    );

    if (isSelected) {
      // Remove item from selection
      setSelectedItems(
        selectedItems.filter((selected) => selected.id !== item.id)
      );
    } else {
      // Add item to selection
      setSelectedItems([...selectedItems, item]);
    }
  };

  const isItemSelected = (item) => {
    return selectedItems.some((selected) => selected.id === item.id);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      toggleDropdown();
    }
  };

  const getDisplayText = () => {
    if (selectedItems.length === 0) {
      return t(placeholder);
    }

    if (showSelectedCount) {
      return `${selectedItems.length} ${t("selected")}`;
    }

    // Show first selected item name
    const firstItem = selectedItems[0];
    const itemText = !nameInAr
      ? firstItem?.title
        ? t(firstItem.title)
        : firstItem?.name
      : firstItem?.title
      ? t(firstItem.title)
      : firstItem?.nameInAr;

    return selectedItems.length > 1
      ? `${itemText} (+${selectedItems.length - 1})`
      : itemText;
  };

  // Render the dropdown menu in a portal
  const renderMenu = () => {
    if (!isOpen) return null;

    return createPortal(
      <ul
        ref={menuRef}
        className={`u-padding_300 dropdown-menu-portal ${openUpward ? "open-upward" : ""}`}
        style={{
          position: "absolute",
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          width: `${menuPosition.width}px`,
        }}
      >
        {Array.isArray(options) &&
          options.map((option, index) => {
            const isSelected = isItemSelected(option);

            return (
              <li
                key={index}
                className={`${titleCssClass} u-padding_100 o-row o-row--gap-size_200 o-row--vertical-align_center ${
                  isSelected ? "selected" : ""
                }`}
                onClick={() => handleItemClick(option)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleItemClick(option);
                  }
                }}
              >
                <CustomCheckbox
                  checked={isSelected}
                  onChange={() => {}} // Handled by li onClick
                  tabIndex={-1}
                  className="checkbox-input"
                />

                <span className="checkbox-label">
                  {!nameInAr
                    ? typeof option === "object"
                      ? t(option?.title) || option?.name
                      : option
                    : typeof option === "object"
                    ? t(option?.title) || option?.nameInAr
                    : option}
                </span>
              </li>
            );
          })}
      </ul>,
      document.body
    );
  };

  return (
    <div
      className={`c-dropdown ${flex ? "full-width-dropdown" : ""}`}
      ref={dropdownRef}
    >
      <div
        ref={toggleRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        className={`toggle ${isOpen ? "opened" : ""} ${thick ? "thick" : ""}`}
        style={disabled ? { cursor: "not-allowed", opacity: 0.6 } : {}}
      >
        <p className="text">{getDisplayText()}</p>
        <img
          src={arrowIcon}
          alt="Dropdown arrow"
          className={`arrow-icon ${isOpen ? "rotate180" : ""}`}
        />
      </div>

      {renderMenu()}
    </div>
  );
};

export default MultiSelecteDropdown;
